import React from 'react';
import { Space, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import TableComponent from '../antTable/table';
import CustomPagination from '../antTable/pagenation';
import {
  deleteGroupData,
  setGroupIsDescending,
  setGroupSelectedItems,
  setGroupSortType,
  setPageGroupNumber,
  setPageGroupSize,
} from 'redux/slices/getOuGroups';
import { SweetSweetAlert } from 'component/sweetalert_components';
import UpdateGroup from 'component/modal_components/update_group.js';
import { SpanButton } from 'component/button_components/button.style';
import { ReactComponent as DeleteIcon } from 'assets/images/SVG/icons/delete-icon.svg';
import { useRefreshGroupTab } from './refreshGroupData';
import ViewGroup from 'component/modal_components/viewGroup';
import TableLoader from 'component/loader_component/tableLoader';
import { refreshToken } from 'redux/slices/authSlice';

export default function GroupsData() {
  const refreshGroupTab = useRefreshGroupTab();
  const { data, loading, totalCount, pageGroupNumber, pageGroupSize, groupSelectedItems } = useSelector(
    (state) => state.getOuGroupData
  );
  const dispatch = useDispatch();
  const { ouAdminRights, portalAdminRights } = useSelector((state) => state.settings);
  const selectedNode = useSelector((state) => state.selectedNode);
  const role = localStorage.getItem('role');
  const isSuperAdmin = role === 'superadmin';
  const canView =
    isSuperAdmin ||
    (role === 'portaladmin' ? portalAdminRights?.portalgroupView ?? true : ouAdminRights?.ouAdmingroupView ?? true);
  const canEdit =
    isSuperAdmin ||
    (role === 'portaladmin' ? portalAdminRights?.portalgroupEdit ?? true : ouAdminRights?.ouAdmingroupEdit ?? true);
  const canDelete =
    isSuperAdmin ||
    (role === 'portaladmin' ? portalAdminRights?.portalgroupDelete ?? true : ouAdminRights?.ouAdmingroupDelete ?? true);
  const canBulkGroupAction =
    isSuperAdmin ||
    (role === 'portaladmin'
      ? portalAdminRights?.portalgroupBulkAction ?? true
      : ouAdminRights?.ouAdmingroupBulkAction ?? true);

  const deleteDomainSweetAlert = async (dnName) => {
    await dispatch(refreshToken());
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure you want to delete?',
      showConfirmButton: true,
      confirmButtonColor: 'var(--bg-button-default-color)',
      width: '400px',
      heightAuto: false,
      showCancelButton: true,
      cancelButtonColor: 'green',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        container: 'custom-swal-container',
        icon: 'custom-swal-icon',
        title: 'custom-swal-title',
      },
    });

    if (result.isConfirmed) {
      try {
        let values = {
          fqdn: selectedNode.domainName,
          dn: [dnName],
        };
        const sweetAlert = await dispatch(deleteGroupData(values));
        if (sweetAlert.payload && sweetAlert.payload.status === 200) {
          SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'success', buttonColor: 'green' });
        } else {
          SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
        }
      } catch (error) {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      }
      refreshGroupTab();
    }
  };

  const columns = [
    {
      title: 'Group Name',
      dataIndex: 'groupName',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: true,
    },
    {
      title: 'GroupType',
      dataIndex: 'groupType',
      sorter: true,
      render: (text) => {
        if (text === 2) {
          return 'Distribution';
        } else {
          return 'Security';
        }
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space>
          {canEdit && <UpdateGroup {...record} />}
          {canView && <ViewGroup {...record} />}
          {canDelete && record.systemFlags !== 1 && (
            <Tooltip title='Delete' color='#597081' className='af-group-delete-tooltip'>
              <SpanButton
                className='af-group-delete-tooltip-button'
                type='primary'
                onClick={() => deleteDomainSweetAlert(record.dnName)}
              >
                <DeleteIcon className='af-group-delete-tooltip-icon' />
              </SpanButton>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleSorting = (_, __, sorter) => {
    if (sorter.order) {
      const isDecending = sorter.order === 'descend';
      dispatch(setGroupIsDescending(isDecending));
      dispatch(setGroupSortType(sorter.field));
    } else {
      dispatch(setGroupSortType(''));
    }
  };

  const startIndex = (pageGroupNumber - 1) * pageGroupSize;
  const endIndex = startIndex + pageGroupSize;

  const handlePagination = async (value, pagination) => {
    dispatch(setPageGroupNumber(value));
    dispatch(setPageGroupSize(pagination));
  };

  const rowSelection = {
    groupSelectedItems,
    onChange: (selectedRowKeys, selectedRows) => {
      dispatch(setGroupSelectedItems(selectedRowKeys));
    },
    selectedRowKeys: groupSelectedItems,
  };

  return (
    <div className='af-group-wrap'>
      <div className='af-group-table-wrap'>
        <TableComponent
          columns={columns}
          data={data}
          rowSelection={canBulkGroupAction ? rowSelection : false}
          rowKey='dnName'
          loading={{
            spinning: loading,
            indicator: <TableLoader />,
          }}
          onChange={handleSorting}
        />
      </div>
      <div className='af-group-pagination-wrap'>
        <CustomPagination
          loading={loading}
          totalItems={totalCount}
          currentPage={pageGroupNumber}
          pageSize={pageGroupSize}
          onPageChange={handlePagination}
          pageSizeOptions={[20, 50, 100, 500]}
          startIndex={startIndex}
          endIndex={endIndex}
        />
      </div>
    </div>
  );
}
